import type { Localization } from '../../dependencies/localization';
import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';
import { getBaseSeoData } from './BaseSeoData';
import type { SeoData } from './types';
import type { BrowseDetailPage } from '@jet-app/music-classical/models';
import { capitalize } from '../string';

export const KNOWN_BROWSE_DETAIL_PAGES = [
    'composers',
    'periods',
    'genres',
    'conductors',
    'orchestras',
    'soloists',
    'ensembles',
    'choirs',
];

/**
 * @method getBrowseDetailSeoData
 * @param {string} url Browse canonical URL
 * @param {BrowseDetailPage} page Browse Detail page object
 * @param {string} entityType Current Browse detail entity
 * @param {ObjectGraph} objectGraph
 * @returns {SeoData}
 */
export function getBrowseDetailSeoData(
    url: string,
    page: BrowseDetailPage,
    entityType: string,
    objectGraph: ObjectGraph,
): SeoData {
    const localization: Localization = inject(
        ObjectGraphType.localization,
        objectGraph,
    );
    const baseSeoData: SeoData = getBaseSeoData(url, objectGraph);
    const siteName: string = localization.string(
        'ASE.Web.MusicClassicalClientJS.Name',
    );

    function getPageTitle() {
        if (entityType === 'playlists') {
            const playlistTitleLoc = localization.string(
                'ASE.Web.MusicClassicalClientJS.Meta.BrowseDetail.Playlists.Title',
                { playlistTitle: page.header.title },
            );

            return localization.string(
                'ASE.Web.MusicClassicalClientJS.ContentA.Dash.ContentB',
                {
                    contentA: playlistTitleLoc,
                    contentB: siteName,
                },
            );
        }

        const titleLocKey = KNOWN_BROWSE_DETAIL_PAGES.includes(entityType)
            ? `ASE.Web.MusicClassicalClientJS.Meta.BrowseDetail.${capitalize(
                  entityType,
              )}.Title`
            : 'ASE.Web.MusicClassicalClientJS.Meta.Browse.Tab.Catalog';

        return localization.string(
            'ASE.Web.MusicClassicalClientJS.ContentA.Dash.ContentB',
            {
                contentA: localization.string(titleLocKey),
                contentB: siteName,
            },
        );
    }

    function getPageDescription() {
        if (entityType === 'playlists') {
            return localization.string(
                'ASE.Web.MusicClassicalClientJS.Meta.BrowseDetail.Playlists.Description',
                { playlistTitle: page.header.title },
            );
        }

        const descLocKey = KNOWN_BROWSE_DETAIL_PAGES.includes(entityType)
            ? `ASE.Web.MusicClassicalClientJS.Meta.BrowseDetail.${capitalize(
                  entityType,
              )}.Description`
            : 'ASE.Web.MusicClassicalClientJS.Meta.Browse.Description.Catalog';

        return localization.string(descLocKey);
    }

    const pageTitle: string = getPageTitle();
    const description: string = getPageDescription();

    /**
     *
     * Generate SEO data object
     *
     */
    return {
        ...baseSeoData,
        pageTitle,
        description,

        /**
         * Social OG Tags
         */
        socialTitle: pageTitle,
        socialDescription: description,

        /**
         *
         * Browse Apple-spcific Values
         *
         */
        appleContentId: undefined,
        appleTitle: pageTitle,
        appleDescription: description,
    };
}

export function addingBrowseDetailSeoData(
    url: string,
    page: BrowseDetailPage,
    entityType: string,
    objectGraph: ObjectGraph,
): BrowseDetailPage {
    return {
        ...page,
        seoData: getBrowseDetailSeoData(url, page, entityType, objectGraph),
    };
}
